import { useState, useRef, useEffect, CSSProperties } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../../../firebase';
import { ref, update } from 'firebase/database';
import { apiFetch } from '../../../utils/api';
import { useBotWorkspace } from '../../../hooks/useBotWorkspace';
import { useDashboardChrome } from '../../../hooks/useDashboardChrome';
import { formatPhone, widgetURL, API_BASE } from '../../../utils/botHelpers';
import SendInviteSection from '../../../components/dashboard/SendInviteSection';

function StatCard({ icon, val, label, accent }: { icon: string; val: string; label: string; accent?: string }) {
  return (
    <div style={{ background: accent ? accent + '15' : 'var(--bg-card)', borderRadius: '12px', border: `1px solid ${accent ? accent + '30' : 'var(--card-border)'}`, boxShadow: 'var(--card-shadow)', padding: '18px' }}>
      <div style={{ fontSize: '16px', marginBottom: '8px' }}>{icon}</div>
      <div style={{ color: 'var(--text-primary)', fontSize: '24px', fontWeight: '700' }}>{val}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginTop: '3px' }}>{label}</div>
    </div>
  );
}

function BarChart({ data, label }: { data: Array<{ date: string; messages: number }>; label: string }) {
  const max = Math.max(...data.map(d => d.messages), 1);
  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', boxShadow: 'var(--card-shadow)', padding: '20px', marginBottom: '16px' }}>
      <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '600', marginBottom: '16px' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '100px' }}>
        {data.slice(-7).map((day, i) => {
          const h = Math.max(4, (day.messages / max) * 100);
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: '600' }}>{day.messages || ''}</div>
              <div style={{ width: '100%', background: '#5B4FE8', height: `${h}px`, borderRadius: '4px 4px 0 0', opacity: day.messages ? 1 : 0.25 }} />
              <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short' })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function OverviewRoute() {
  const navigate = useNavigate();
  const { bot, analytics, analyticsLoading, contacts, refreshBot } = useBotWorkspace();
  const { currentUser } = useDashboardChrome();

  // Email-only local state — Overview is the only consumer of the inbox.
  const [emailInbox, setEmailInbox] = useState<any[]>([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [replyingUid, setReplyingUid] = useState<number | null>(null);
  const pollCountdownRef = useRef(120);
  const countdownBarRef = useRef<HTMLDivElement>(null);
  const countdownTextRef = useRef<HTMLDivElement>(null);

  const type = bot?.botType === 'facebook' && bot?.fbMode === 'comments' ? 'facebook_comments' : (bot?.botType || 'whatsapp');

  const loadEmailInbox = async (botId: string) => {
    setInboxLoading(true);
    pollCountdownRef.current = 120;
    try {
      const res = await apiFetch(`/api/email-bot/inbox/${botId}`);
      const data = await res.json();
      if (data.success) setEmailInbox(data.emails || []);
      else alert('Inbox error: ' + data.error);
    } catch (e: any) {
      console.error('Inbox load error:', e);
    } finally {
      setInboxLoading(false);
    }
  };

  // Load inbox once for email bots, then auto-refresh every 2 minutes with a
  // live countdown bar — DOM refs updated directly (no setState) so the 1s
  // tick doesn't re-render the whole route or reset scroll position.
  useEffect(() => {
    if (type !== 'email' || !bot) return;
    loadEmailInbox(bot.id);
    pollCountdownRef.current = 120;
    const tick = setInterval(() => {
      pollCountdownRef.current -= 1;
      const s = pollCountdownRef.current;
      if (countdownBarRef.current) countdownBarRef.current.style.width = `${((120 - s) / 120) * 100}%`;
      if (countdownTextRef.current) countdownTextRef.current.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
      if (s <= 0) { pollCountdownRef.current = 120; loadEmailInbox(bot.id); }
    }, 1000);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, bot?.id]);

  if (!bot) return null;
  if (analyticsLoading) return <div style={{ color: 'var(--text-faint)', fontSize: '13px', textAlign: 'center', padding: '40px' }}>Loading...</div>;

  if (type === 'whatsapp') return (
    <>
      {(bot as any).isTestNumber && (() => {
        const kw = (bot as any).testKeyword || '';
        const displayNum = ((bot as any).testDisplayNumber || '').replace(/\D/g, '');
        const inviteLink = displayNum && kw ? `https://wa.me/${displayNum}?text=${encodeURIComponent(kw)}` : null;
        return (
          <div style={{ background: '#5B4FE810', border: '1px solid #5B4FE830', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }}>
            <div style={{ fontWeight: '600', fontSize: '12px', color: '#818cf8', marginBottom: '6px' }}>🧪 Using Autopilot shared test number</div>
            {kw && (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: inviteLink ? '8px' : 0 }}>
                Keyword: <strong style={{ color: 'var(--text-primary)' }}>{kw}</strong>
                {inviteLink && <> · <a href={inviteLink} target="_blank" rel="noreferrer" style={{ color: '#818cf8' }}>Test on WhatsApp ↗</a></>}
              </div>
            )}
            {inviteLink && (
              <div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <code style={{ flex: 1, background: 'var(--bg-element)', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{inviteLink}</code>
                  <button onClick={() => navigator.clipboard.writeText(inviteLink)}
                    style={{ background: 'var(--bg-element)', border: '1px solid var(--card-border)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Copy</button>
                </div>
                <SendInviteSection botId={bot.id} userName={currentUser?.displayName || currentUser?.email || 'a user'} />
              </div>
            )}
            <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-faint)' }}>
              Ready to go live with your own number? Open <strong>Settings</strong> → switch to Meta Business or Manual.
            </div>
          </div>
        );
      })()}
      {!(bot as any).isTestNumber && (bot as any).phoneNumberId && (() => {
        const displayNum = ((bot as any).whatsappDisplayNumber || '').replace(/\D/g, '');
        const inviteLink = displayNum ? `https://wa.me/${displayNum}` : null;
        const isMetaSandboxNumber = /^1?555/.test(displayNum);
        const accent = isMetaSandboxNumber ? '#f59e0b' : '#10b981';
        return (
          <div style={{ background: `${accent}10`, border: `1px solid ${accent}30`, borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }}>
            <div style={{ fontWeight: '600', fontSize: '12px', color: accent, marginBottom: '6px' }}>
              {isMetaSandboxNumber ? '🧪 Connected — but this is a Meta sandbox test number' : '✅ Live on your own WhatsApp number'}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '600', marginBottom: '8px' }}>
              {(bot as any).whatsappDisplayNumber || (bot as any).phoneNumberId}
              {(bot as any).whatsappVerifiedName && <span style={{ color: 'var(--text-muted)', fontWeight: '400' }}> · {(bot as any).whatsappVerifiedName}</span>}
            </div>
            {isMetaSandboxNumber && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', lineHeight: 1.5 }}>
                This number was auto-assigned by Meta for testing — it isn't a real, reachable phone number, so invites and customer messages won't actually work. In Meta Business Manager, add your real business phone number to this WhatsApp Business Account, then reconnect.
              </div>
            )}
            {inviteLink && (
              <div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <code style={{ flex: 1, background: 'var(--bg-element)', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{inviteLink}</code>
                  <button onClick={() => navigator.clipboard.writeText(inviteLink)}
                    style={{ background: 'var(--bg-element)', border: '1px solid var(--card-border)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Copy</button>
                  <a href={inviteLink} target="_blank" rel="noreferrer" style={{ color: '#10b981', fontSize: '11px', whiteSpace: 'nowrap' }}>Open ↗</a>
                </div>
                <SendInviteSection botId={bot.id} userName={currentUser?.displayName || currentUser?.email || 'a user'} />
              </div>
            )}
          </div>
        );
      })()}
      {!analytics ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '40px', marginBottom: '14px' }}>💬</div>
          <div style={{ color: 'var(--text-faint)', fontSize: '14px' }}>No messages yet. Share your WhatsApp number to get started.</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '20px' }}>
            <StatCard icon="💬" val={analytics.total.totalMessages.toLocaleString()} label="Messages received" />
            <StatCard icon="👥" val={analytics.total.totalUsers.toLocaleString()} label="Unique contacts" />
            <StatCard icon="⚡" val={analytics.total.totalInteractions != null ? `${Math.round((analytics.total.totalInteractions / (analytics.total.totalMessages || 1)) * 100)}%` : `${Math.round(((analytics.total.totalMessages - (analytics.recentConversations?.filter((c: any) => c.message === '[SYSTEM] agent_takeover').length || 0)) / (analytics.total.totalMessages || 1)) * 100)}%`} label="Auto-handled" accent="#10b981" />
            <StatCard icon="💰" val={`$${Math.round(analytics.total.totalMessages * 0.5)}`} label="Est. savings" accent="#5B4FE8" />
          </div>
          <BarChart data={analytics.daily} label="Messages last 7 days" />
          <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '700' }}>Recent conversations</div>
              <button onClick={() => navigate(`/dashboard/bots/${bot.id}/conversations`)} style={{ background: 'var(--bg-element)', border: '1px solid var(--border)', color: 'var(--text-primary)', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>View all →</button>
            </div>
            {analytics.recentConversations.slice(0, 4).map((conv, i) => {
              const contact = contacts[conv.userId] || {};
              const displayName = contact.name || contact.customer_name;
              return (
                <div key={i} style={{ padding: '10px 12px', background: 'var(--bg-element)', borderRadius: '9px', marginBottom: '8px', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '5px', fontWeight: '600' }}>
                    {displayName ? `${displayName} · ` : ''}{formatPhone(conv.userId)} · {new Date(conv.timestamp).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-primary)', marginBottom: '2px' }}>👤 {conv.message}</div>
                  <div style={{ fontSize: '12px', color: '#5B4FE8', marginTop: '2px', fontWeight: '500' }}>🤖 {conv.response.slice(0, 100)}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );

  if (type === 'facebook') return (
    <>
      {!analytics ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '40px', marginBottom: '14px' }}>📘</div>
          <div style={{ color: 'var(--text-faint)', fontSize: '14px' }}>No messages yet. Make sure your Facebook Page is connected.</div>
          {bot.fbPageId && <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '8px' }}>Page ID: {bot.fbPageId}</div>}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '20px' }}>
            <StatCard icon="💬" val={analytics.total.totalMessages.toLocaleString()} label="Messages received" />
            <StatCard icon="👥" val={analytics.total.totalUsers.toLocaleString()} label="Followers engaged" />
            <StatCard icon="⚡" val={`${Math.round((analytics.total.totalInteractions / (analytics.total.totalMessages || 1)) * 100)}%`} label="Auto-replied" accent="#1877f2" />
            <StatCard icon="📈" val={(analytics.total.totalMessages / (analytics.total.totalUsers || 1)).toFixed(1)} label="Msgs per user" />
          </div>
          <BarChart data={analytics.daily} label="Messages last 7 days" />
        </>
      )}
    </>
  );

  if (type === 'facebook_comments') return (
    <>
      {!analytics ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '40px', marginBottom: '14px' }}>📝</div>
          <div style={{ color: 'var(--text-faint)', fontSize: '14px' }}>No comments replied to yet. Comments on your Page's posts will show up here once the bot answers one.</div>
          {bot.fbPageId && <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '8px' }}>Page ID: {bot.fbPageId}</div>}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '20px' }}>
            <StatCard icon="📝" val={analytics.total.totalMessages.toLocaleString()} label="Comments replied to" />
            <StatCard icon="👥" val={analytics.total.totalUsers.toLocaleString()} label="Unique commenters" />
            <StatCard icon="⚡" val={`${Math.round((analytics.total.totalInteractions / (analytics.total.totalMessages || 1)) * 100)}%`} label="Auto-replied" accent="#1877f2" />
          </div>
          <BarChart data={analytics.daily} label="Comments last 7 days" />
        </>
      )}
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <button onClick={() => navigate(`/dashboard/bots/${bot.id}/comments`)} style={{ background: '#5B4FE8', border: 'none', color: 'white', padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          View all comments →
        </button>
      </div>
    </>
  );

  if (type === 'website') return (
    <>
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Your embed code</div>
          <button onClick={() => { navigator.clipboard.writeText(`<script src="${widgetURL}/widget.js" data-bot-id="${bot.id}"${API_BASE ? ` data-api-url="${API_BASE}"` : ''}></script>`); alert('Copied!'); }}
            style={{ background: '#5B4FE8', border: 'none', color: 'white', padding: '5px 14px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>Copy</button>
        </div>
        <div style={{ background: 'var(--bg-page)', borderRadius: '8px', padding: '12px', border: '1px solid var(--card-border)' }}>
          <code style={{ color: '#818cf8', fontSize: '12px', wordBreak: 'break-all' }}>
            {`<script src="${widgetURL}/widget.js" data-bot-id="${bot.id}"${API_BASE ? ` data-api-url="${API_BASE}"` : ''}></script>`}
          </code>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '8px' }}>Paste before {'</body>'} · {bot.websiteUrl || 'No website set'}</div>
      </div>
      {analytics && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '20px' }}>
            <StatCard icon="💬" val={analytics.total.totalMessages.toLocaleString()} label="Visitor chats" />
            <StatCard icon="👥" val={analytics.total.totalUsers.toLocaleString()} label="Unique visitors" />
            <StatCard icon="⚡" val={analytics.total.totalMessages > 0 ? `${Math.round(((analytics.total.totalInteractions || analytics.total.totalMessages) / analytics.total.totalMessages) * 100)}%` : '—'} label="FAQ hit rate" accent="#10b981" />
            <StatCard icon="📅" val={analytics.daily.slice(-1)[0]?.messages.toString() || '0'} label="Chats today" />
          </div>
          <BarChart data={analytics.daily} label="Visitor chats last 7 days" />
        </>
      )}
    </>
  );

  if (type === 'ai') return (
    <>
      {/* Embed code — website AI bots only */}
      {bot.aiChannel === 'website' && (
        <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '20px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: '600' }}>Your embed code</div>
            <button onClick={() => { navigator.clipboard.writeText(`<script src="${widgetURL}/widget.js" data-bot-id="${bot.id}"${API_BASE ? ` data-api-url="${API_BASE}"` : ''}></script>`); alert('Copied!'); }}
              style={{ background: '#5B4FE8', border: 'none', color: 'white', padding: '5px 14px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>Copy</button>
          </div>
          <div style={{ background: 'var(--bg-page)', borderRadius: '8px', padding: '12px', border: '1px solid var(--card-border)' }}>
            <code style={{ color: '#818cf8', fontSize: '12px', wordBreak: 'break-all' }}>
              {`<script src="${widgetURL}/widget.js" data-bot-id="${bot.id}"${API_BASE ? ` data-api-url="${API_BASE}"` : ''}></script>`}
            </code>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '8px' }}>Paste before {'</body>'} on your website</div>
        </div>
      )}
      {/* AI bot info strip */}
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '16px', marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '120px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-faint)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Channel</div>
          <div style={{ color: 'var(--text-primary)', fontSize: '13px' }}>{bot.aiChannel === 'whatsapp' ? '💬 WhatsApp' : bot.aiChannel === 'facebook' ? '📘 Facebook' : '🌐 Website'}</div>
        </div>
        <div style={{ flex: 1, minWidth: '120px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-faint)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Knowledge base</div>
          <div style={{ color: ((bot as any).aiConfig?.datasetId || (bot as any).aiConfig?.knowledgeBase) ? '#10b981' : '#f59e0b', fontSize: '13px' }}>
            {(bot as any).aiConfig?.datasetId ? '✓ Dataset selected'
              : (bot as any).aiConfig?.knowledgeBase
                ? `✓ ${((bot as any).aiConfig.knowledgeBase as string).length.toLocaleString()} chars (inline)`
                : '⚠️ Not set — open Settings'}
          </div>
        </div>
      </div>
      {/* Stats */}
      {analyticsLoading && <div style={{ color: 'var(--text-faint)', fontSize: '13px', textAlign: 'center', padding: '40px' }}>Loading...</div>}
      {!analyticsLoading && analytics && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '20px' }}>
            <StatCard icon="💬" val={analytics.total.totalMessages.toLocaleString()} label="AI conversations" />
            <StatCard icon="👥" val={analytics.total.totalUsers.toLocaleString()} label="Unique users" />
            <StatCard icon="⚡" val={analytics.total.totalMessages > 0 ? `${Math.round(((analytics.total.totalInteractions || analytics.total.totalMessages) / analytics.total.totalMessages) * 100)}%` : '—'} label="Response rate" accent="#10b981" />
            <StatCard icon="📅" val={analytics.daily.slice(-1)[0]?.messages.toString() || '0'} label="Chats today" />
          </div>
          <BarChart data={analytics.daily} label="Conversations last 7 days" />
        </>
      )}
      {!analyticsLoading && !analytics && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '40px', marginBottom: '14px' }}>🤖</div>
          <div style={{ color: 'var(--text-faint)', fontSize: '14px' }}>No conversations yet. Share your {bot.aiChannel === 'whatsapp' ? 'WhatsApp number' : bot.aiChannel === 'facebook' ? 'Facebook page' : 'widget embed code'} to get started.</div>
        </div>
      )}
    </>
  );

  if (type === 'email') {
    const autoMode: string = (bot as any).autoReplyMode || 'all';
    const setAutoMode = async (mode: string) => {
      const payload: any = { autoReplyMode: mode };
      if (mode === 'new') payload.autoReplyFrom = Date.now();
      await update(ref(db, `bots/${bot.id}`), payload);
      refreshBot();
    };
    const modeBtn = (label: string, val: string) => (
      <button key={val} onClick={() => setAutoMode(val)} style={{
        padding: '6px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
        background: autoMode === val ? '#5B4FE8' : 'var(--bg-element)',
        color: autoMode === val ? 'white' : 'var(--text-secondary)',
      }}>{label}</button>
    );
    const timeAgo = (iso: string) => {
      const diff = Date.now() - new Date(iso).getTime();
      if (diff < 60000) return 'just now';
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      return `${Math.floor(diff / 86400000)}d ago`;
    };
    const handleReply = async (email: any) => {
      setReplyingUid(email.uid);
      try {
        const res = await apiFetch(`/api/email-bot/reply/${bot.id}`, {
          method: 'POST',
          body: JSON.stringify({ uid: email.uid, fromAddress: email.fromAddress, subject: email.subject, messageId: email.messageId, bodyText: email.bodyText }),
        });
        const data = await res.json();
        if (data.success) {
          setEmailInbox(prev => prev.map(e => e.uid === email.uid ? { ...e, seen: true, replied: true } : e));
        } else {
          alert('Reply failed: ' + data.error);
        }
      } catch (e: any) {
        alert('Reply error: ' + e.message);
      } finally {
        setReplyingUid(null);
      }
    };
    return (
      <>
        {/* Auto-reply mode */}
        <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '16px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: '4px' }}>Auto-reply</div>
          <div style={{ display: 'flex', gap: '6px', background: 'var(--bg-element)', padding: '4px', borderRadius: '8px' }}>
            {modeBtn('All unread', 'all')}
            {modeBtn('Only new', 'new')}
            {modeBtn('None', 'none')}
          </div>
          {autoMode !== 'none' && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '80px', height: '4px', borderRadius: '2px', background: 'var(--bg-element)', overflow: 'hidden' }}>
                <div ref={countdownBarRef} style={{ height: '100%', borderRadius: '2px', background: '#5B4FE8', width: '0%', transition: 'width 1s linear' }} />
              </div>
              <div ref={countdownTextRef} style={{ fontSize: '11px', color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums' } as CSSProperties}>2:00</div>
            </div>
          )}
          {autoMode === 'none' && <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginLeft: 'auto' }}>No auto-replies — use Reply button below</div>}
        </div>

        {/* Inbox */}
        <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '700' }}>
              Inbox
              {emailInbox.length > 0 && <span style={{ marginLeft: '8px', background: 'var(--bg-element)', color: 'var(--text-muted)', fontSize: '11px', padding: '2px 8px', borderRadius: '10px' }}>{emailInbox.length}</span>}
            </div>
            <button onClick={() => loadEmailInbox(bot.id)} disabled={inboxLoading}
              style={{ background: 'none', border: '1px solid var(--card-border)', color: 'var(--text-muted)', padding: '5px 12px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
              {inboxLoading ? 'Loading...' : '↺ Refresh'}
            </button>
          </div>

          {inboxLoading && emailInbox.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-faint)', fontSize: '13px' }}>Loading inbox...</div>
          ) : emailInbox.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
              <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '8px' }}>No emails loaded yet</div>
              <button onClick={() => loadEmailInbox(bot.id)}
                style={{ background: '#5B4FE8', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>Load inbox</button>
            </div>
          ) : (
            emailInbox.map((email: any) => {
              const isFiltered = /noreply|no-reply|mailer-daemon|postmaster|notification|unsubscribe|bounce|autorespond|donotreply|do-not-reply|newsletter|digest|marketing|promo|updates-noreply|alerts|security@|support@twitter|info@twitter|@facebookmail\.com|@linkedin\.com|@instagram\.com|@google\.com|@github\.com|@elfsight|@termly|@zimpapers|@paynow/i.test(email.fromAddress)
                || /^(Delivery Status Notification|Undelivered Mail|Out of Office|Auto:|Automatic reply|Fw:|FW:|Fwd:|FWD:)/i.test(email.subject);
              const receivedTs = email.internalDate ? new Date(email.internalDate).getTime() : new Date(email.date).getTime();
              const autoReplyFrom: number = (bot as any).autoReplyFrom || 0;
              const willAutoReply = !isFiltered && !email.repliedAt && !email.seen && (
                autoMode === 'all' ||
                (autoMode === 'new' && receivedTs > autoReplyFrom)
              );
              const isReplied = !!email.repliedAt;

              return (
              <div key={email.uid} style={{
                padding: '12px 14px', borderRadius: '10px', marginBottom: '8px',
                border: `1px solid ${isReplied ? 'rgba(16,185,129,0.25)' : willAutoReply ? 'rgba(91,79,232,0.3)' : 'var(--border)'}`,
                background: isReplied ? 'rgba(16,185,129,0.04)' : 'var(--bg-element)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                      {!email.seen && !isReplied && <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: willAutoReply ? '#5B4FE8' : '#6b7280', flexShrink: 0 }} />}
                      <div style={{ fontSize: '12px', fontWeight: email.seen ? '500' : '700', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.subject}</div>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>{email.from}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-faint)', lineHeight: '1.4', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>{email.preview}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>{timeAgo(email.internalDate || email.date)}</div>
                    {isReplied ? (
                      <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px' }}>✓ Replied</span>
                    ) : willAutoReply ? (
                      <span style={{ fontSize: '11px', color: '#818cf8', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#5B4FE8', animation: 'pulse 1.5s infinite' }} />
                        Will auto-reply
                      </span>
                    ) : !isFiltered ? (
                      <button onClick={() => handleReply(email)} disabled={replyingUid === email.uid}
                        style={{ background: 'var(--bg-page)', color: 'var(--text-secondary)', border: '1px solid var(--card-border)', padding: '5px 12px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        {replyingUid === email.uid ? '...' : '↩ Reply with AI'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      </>
    );
  }

  if (type === 'voice') return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎙️</div>
      <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>{bot.businessName}</div>
      <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '24px' }}>Voice bot · {bot.websiteUrl || 'No website set'}</div>
      <Link to={`/builder/voice/${bot.id}`} style={{ display: 'inline-block', background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white', padding: '10px 24px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600' }}>
        🎙️ Edit voice script
      </Link>
    </div>
  );

  return null;
}
